type Body = { sid: string; events?: unknown[]; errors?: unknown[]; replay?: { seq: number; events: unknown[] } };
type Send = (body: string, final: boolean) => Promise<boolean>;

// Telemetry is best-effort: a batch that fails is dropped, not retried, so an
// outage never turns into a backlog the browser keeps re-sending. Events and
// replay go as separate requests because a replay chunk can be megabytes and
// must not drag a small batch out of keepalive's 64KB.
const SEQ_KEY = "genesis-telemetry-replay-seq";

// The next replay chunk number for a session. The session id survives a reload
// (it lives in sessionStorage), so the counter has to as well: starting again
// at 0 would overwrite the chunks the page already sent under the same id.
function loadSeq(storage: Storage | null, sid: string): number {
  try {
    const saved = JSON.parse(storage?.getItem(SEQ_KEY) ?? "null") as { sid: string; next: number } | null;
    return saved && saved.sid === sid && Number.isInteger(saved.next) ? saved.next : 0;
  } catch {
    return 0;
  }
}

function saveSeq(storage: Storage | null, sid: string, next: number) {
  try { storage?.setItem(SEQ_KEY, JSON.stringify({ sid, next })); } catch { /* private mode */ }
}

function defaultStorage(): Storage | null {
  try { return typeof sessionStorage === "undefined" ? null : sessionStorage; } catch { return null; }
}

export function createQueue({ send, sid, intervalMs = 10_000, maxEvents = 200, maxErrors = 50, storage = defaultStorage() }: {
  send: Send; sid: () => string; intervalMs?: number; maxEvents?: number; maxErrors?: number; storage?: Storage | null;
}) {
  let events: unknown[] = [];
  let errors: unknown[] = [];
  let frames: unknown[] = [];
  let seq = 0;
  let lastSid: string | null = null;

  async function flush(final = false) {
    // Asking for the id counts as activity, so an idle tab must not ask.
    if (!events.length && !errors.length && !frames.length) return;
    const id = sid();
    if (id !== lastSid) { lastSid = id; seq = loadSeq(storage, id); }
    if (events.length || errors.length) {
      const body: Body = { sid: id };
      if (events.length) body.events = events;
      if (errors.length) body.errors = errors;
      events = []; errors = [];
      await send(JSON.stringify(body), final).catch(() => false);
    }
    if (frames.length) {
      const body: Body = { sid: id, replay: { seq: seq++, events: frames } };
      saveSeq(storage, id, seq);
      frames = [];
      await send(JSON.stringify(body), final).catch(() => false);
    }
  }

  const timer = setInterval(() => void flush(), intervalMs);
  return {
    event(e: Record<string, unknown>) { if (events.length < maxEvents) events.push({ ...e, t: new Date().toISOString() }); },
    error(e: Record<string, unknown>) { if (errors.length < maxErrors) errors.push({ ...e, t: new Date().toISOString() }); },
    replay(batch: unknown[]) { frames.push(...batch); },
    flush,
    stop() { clearInterval(timer); },
  };
}
